import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ComponentType,
  ReactNode,
} from 'react';
import {
  getAuth,
  onIdTokenChanged,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from 'firebase/auth';
import { useRouter } from 'next/router';
import LoadingIndicator from '@/components/generic/LoadingIndicator';
import { initFirebase } from './firebase';

export interface User {
  id: string | null;
  email: string | null;
  displayName: string | null;
  getIdToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
  clientInitialized: boolean;
}

const defaultUser: User = {
  id: null,
  email: null,
  displayName: null,
  getIdToken: async () => null,
  signOut: async () => {},
  clientInitialized: false,
};

const AuthContext = createContext<User>(defaultUser);

export function useUser(): User {
  return useContext(AuthContext);
}

export enum AuthAction {
  SHOW_LOADER = 'showLoader',
  REDIRECT_TO_LOGIN = 'redirectToLogin',
}

export interface WithUserOptions {
  whenUnauthedBeforeInit?: AuthAction;
  whenUnauthedAfterInit?: AuthAction;
  authPageURL?: string;
}

const COOKIE_NAME = 'firebaseToken';
const COOKIE_FLAGS = 'path=/; SameSite=Strict; secure';

function setFirebaseTokenCookie(token: string) {
  document.cookie = `${COOKIE_NAME}=${token}; ${COOKIE_FLAGS}`;
}

export function clearFirebaseTokenCookie() {
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${COOKIE_FLAGS}`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User>(defaultUser);
  const firebaseUserRef = useRef<FirebaseUser | null>(null);

  useEffect(() => {
    initFirebase();

    const auth = getAuth();
    const getIdToken = () =>
      firebaseUserRef.current ? firebaseUserRef.current.getIdToken() : Promise.resolve(null);
    const signOut = () => firebaseSignOut(auth);

    // onIdTokenChanged fires on login, logout, AND token refresh (~every 55 min).
    // We use it instead of onAuthStateChanged so we can keep the firebaseToken cookie
    // up to date — Django middleware reads it to authenticate /docs requests.
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      firebaseUserRef.current = firebaseUser;
      if (firebaseUser) {
        const token = await firebaseUser.getIdToken();
        setFirebaseTokenCookie(token);

        setUser((prev) => {
          if (
            prev.clientInitialized &&
            prev.id === firebaseUser.uid &&
            prev.email === firebaseUser.email &&
            prev.displayName === firebaseUser.displayName
          ) {
            return prev;
          }
          return {
            id: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            getIdToken,
            signOut,
            clientInitialized: true,
          };
        });
      } else {
        clearFirebaseTokenCookie();

        setUser((prev) => {
          if (prev.clientInitialized && prev.id === null) {
            return prev;
          }
          return {
            ...defaultUser,
            getIdToken,
            signOut,
            clientInitialized: true,
          };
        });
      }
    });

    return unsubscribe;
  }, []);

  return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>;
}

function RedirectToLogin({ url }: { url: string }) {
  const router = useRouter();
  useEffect(() => {
    router.replace(url);
  }, [router, url]);
  return <LoadingIndicator />;
}

export function withUser<P extends object>(options?: WithUserOptions) {
  return (WrappedComponent: ComponentType<P>): ComponentType<P> => {
    const WithUserComponent = (props: P) => {
      const user = useUser();

      const showLoaderBeforeInit = options?.whenUnauthedBeforeInit === AuthAction.SHOW_LOADER;
      const redirectAfterInit = options?.whenUnauthedAfterInit === AuthAction.REDIRECT_TO_LOGIN;

      // Auth not yet initialized
      if (!user.clientInitialized) {
        if (showLoaderBeforeInit) {
          return <LoadingIndicator />;
        }
        return <WrappedComponent {...props} />;
      }

      // Auth initialized but user not logged in
      if (!user.id && redirectAfterInit) {
        return <RedirectToLogin url={options?.authPageURL || '/'} />;
      }

      return <WrappedComponent {...props} />;
    };

    WithUserComponent.displayName = `withUser(${
      WrappedComponent.displayName || WrappedComponent.name || 'Component'
    })`;

    return WithUserComponent;
  };
}
